import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    output,
} from '@angular/core';
import { TelXLButtonComponent, TelXLPanelComponent } from '@dxp/elements';
import { IAttachment, IFileEntry } from '@dxp/core/models';
import { OverlayService, TelXLContentPreviewService } from '@dxp/components';
import { mediaIcon } from '@dxp/core/helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TelXLMultipleAttachmentsComponent } from './multiple-attachments/multiple-attachments.component';

export interface IAttachmentFile {
    id: string;
    size: number;
    name: string;
    url: string;
    type: string;
    path?: string;
}

@Component({
    selector: 'telxl-shared-attachments',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TelXLPanelComponent, TelXLButtonComponent],
    templateUrl: './attachments.component.html',
    styleUrl: './attachments.component.scss',
})
export class TelXLAttachmentsComponent {
    readonly #destroyRef = inject(DestroyRef);
    readonly #previewService = inject(TelXLContentPreviewService);
    readonly #overlayService = inject(OverlayService);

    internal = input<IFileEntry[]>([]);
    external = input<IAttachment[]>([]);

    delete = output<IFileEntry>();
    multiple = output();

    files = computed<IAttachmentFile[]>(() => {
        if (this.external().length) {
            return this.external().map(a => ({
                id: a.contentUrl ?? a.name ?? '',
                name: a.name ?? '',
                url: a.contentUrl,
                type: a.contentType ?? 'plain/text',
                size: 0,
            }));
        }

        return this.internal().map(a => ({
            id: a.id,
            name: a.file.name,
            url: a.url ?? '',
            type: a.file.type,
            path: a.response?.data.files[0],
            size: a.file.size,
        }));
    });

    size(file: IAttachmentFile): string {
        const bytes = file.size ?? 0;
        const mb = bytes / (1024 * 1024);
        const kb = bytes / 1024;

        return mb >= 1
            ? `${Math.round(mb)}mb`
            : kb >= 1
              ? `${Math.round(kb)}kb`
              : `${Math.round(bytes)}b`;
    }

    totalSize(): string {
        const bytes = this.files().reduce(
            (total: number, file: IAttachmentFile) => {
                return total + file.size;
            },
            0,
        );
        const mb = bytes / (1024 * 1024);
        const kb = bytes / 1024;

        return mb >= 1
            ? `${Math.round(mb)}mb`
            : kb >= 1
              ? `${Math.round(kb)}kb`
              : `${Math.round(bytes)}b`;
    }

    fileType(file: IAttachmentFile): string {
        return mediaIcon(file.name);
    }

    isExternal = computed<boolean>(() => this.external().length !== 0);

    canPreview(file: IAttachmentFile): boolean {
        return this.#previewService.canPreview(file.type, file.name);
    }

    onPreviewFile(file: IAttachmentFile) {
        if (!this.canPreview(file)) return;

        this.#previewService.open({
            url: file.url ?? '',
            contentType: file.type,
            fileName: file.name,
        });
    }

    onFileDownload(file: IAttachmentFile) {
        window.location.href = file.url;
    }

    onFileDeleted(file: IAttachmentFile) {
        const entry = this.internal().find(i => i.id === file.id);

        if (entry) {
            this.delete.emit(entry);
        }
    }

    onViewMultiple() {
        console.log('Test');

        if (this.isExternal()) {
            this.#overlayService
                .open$(TelXLMultipleAttachmentsComponent, {
                    inputs: {
                        files: this.files(),
                    },
                })
                .pipe(takeUntilDestroyed(this.#destroyRef))
                .subscribe();
        } else {
            this.multiple.emit();
        }
    }
}
